import { useMemo } from "react";
import { Memory } from "../../../api/generated";
import { getMemories } from "../../../api/memory";
import { Path } from "../../../routes/PagePath";
import { getAnswerers } from "../../../api/answerer";
import { useNavigate } from "react-router-dom";
import { useInfiniteQuery, InfiniteData } from "@tanstack/react-query";
import { CacheKey } from "../../../constants/CacheKey";
import { useIncrementLikedCount } from "./useIncrementLikedCount";

export type PaginationMemory = {
    memories: Memory[];
    hasNextPage: boolean;
    nextPageToken: string | undefined;
};

// FIXME: 用途ごとに hook を分割し、且つ tanstack query を使う
export const useMemories = () => {
    const limit = 10;
    const navigate = useNavigate();

    // 回答者がいない or 利用規約に同意していない回答者が1人以上いるときに別ページに遷移させる
    const answererValidateAndRedirect = async (
        memoryList: Memory[]
    ): Promise<void> => {
        // 記憶件数が1件でもある場合は何もしない
        if (memoryList.length >= 1) {
            return;
        }

        const answerers = await getAnswerers();
        if (answerers?.length === 0) {
            navigate(Path.answerers);
            return;
        }

        const termsNotAgreedAnswerers = answerers.filter(
            (x) => !x.isPrivacyAndUsageTermsAgreed
        );
        // 利用規約に同意していない回答者が1人でもいる場合、利用規約同意リマインドページに遷移する。
        if (termsNotAgreedAnswerers?.length >= 1) {
            navigate(Path.useStartMailRemind);
            return;
        }
    };

    const { data, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } =
        useInfiniteQuery(
            CacheKey.memories,
            ({ pageParam = undefined }) => getMemories(pageParam, limit),
            {
                getNextPageParam: (lastPage: PaginationMemory) =>
                    lastPage.hasNextPage ? lastPage.nextPageToken : undefined,
                onSuccess: (infiniteData: InfiniteData<PaginationMemory>) => {
                    if (infiniteData.pages.length === 1) {
                        void answererValidateAndRedirect(
                            infiniteData.pages[0].memories
                        );
                    }
                },
                onError: () => {
                    window.location.href = Path.error;
                },
            }
        );

    const memories = useMemo(() => {
        const pages = data?.pages;
        if (!pages) return [];
        return pages.map((p) => p.memories).flat();
    }, [data]);

    const { incrementLikedCount, tempLikedMemoryIds } =
        useIncrementLikedCount(memories);

    return {
        memories: memories ?? [],
        isMemoriesLoading: isLoading,
        incrementLikedCount,
        tempLikedMemoryIds,
        hasNextPage,
        fetchNextPage,
        isFetchingNextPage,
    };
};
